/* FM Price Slider (18-Jun-2026)
 * ============================
 * A green dual-thumb range slider that drives Shopify's Search & Discovery
 * price-range filter. Replaces Dawn's From/To number inputs.
 *
 * Why a custom element: assets/facets.js re-renders the filter sidebar via
 * AJAX (innerHTML), so any inline init script wouldn't re-run. As a custom
 * element, connectedCallback fires automatically each time a fresh
 * <fm-price-slider> is injected — no re-binding needed.
 *
 * Markup (rendered by snippets/price-facet.liquid):
 *   <fm-price-slider data-floor data-cap data-min data-max data-symbol>
 *     <div class="fm-price__track">
 *       <div class="fm-price__fill" data-fm-price-fill></div>
 *       <input type="range" data-fm-price-min>   (UI only — no name)
 *       <input type="range" data-fm-price-max>   (UI only — no name)
 *     </div>
 *     <div class="fm-price__values">
 *       <span data-fm-price-minlabel></span><span data-fm-price-maxlabel></span>
 *     </div>
 *     <input type="hidden" name="filter.v.price.gte" data-fm-price-gte disabled>
 *     <input type="hidden" name="filter.v.price.lte" data-fm-price-lte disabled>
 *   </fm-price-slider>
 *
 * Behaviour:
 *   • The two range inputs are UI only; the hidden inputs carry the real
 *     filter params. They are enabled (and submitted) ONLY when a thumb is
 *     moved off its extreme:
 *       - min at the floor  -> no `gte` sent (products below the floor still show)
 *       - max at the cap    -> no `lte` sent (the cap reads "£500+", i.e. no
 *         upper bound — everything above is included).
 *   • Dragging updates the green fill + labels live, but does NOT submit
 *     (input events are stopped). Releasing a thumb (`change`) syncs the
 *     hidden inputs and dispatches a bubbling `input` event, which
 *     facets.js debounces into one AJAX filter request.
 */
(function () {
  if (window.customElements && customElements.get('fm-price-slider')) return;

  class FmPriceSlider extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      this.floor = parseFloat(this.dataset.floor);
      this.cap = parseFloat(this.dataset.cap);
      if (isNaN(this.floor)) this.floor = 0;
      if (isNaN(this.cap) || this.cap <= this.floor) this.cap = this.floor + 100;
      this.symbol = this.dataset.symbol || '£';
      this.step = 5;

      this.minRange = this.querySelector('[data-fm-price-min]');
      this.maxRange = this.querySelector('[data-fm-price-max]');
      this.track = this.querySelector('.fm-price__track');
      this.fill = this.querySelector('[data-fm-price-fill]');
      this.thumbRadius = 9; // half the 18px thumb — the inset at each track end
      this.minLabel = this.querySelector('[data-fm-price-minlabel]');
      this.maxLabel = this.querySelector('[data-fm-price-maxlabel]');
      this.gte = this.querySelector('[data-fm-price-gte]');
      this.lte = this.querySelector('[data-fm-price-lte]');
      if (!this.minRange || !this.maxRange) return;

      var dmin = parseFloat(this.dataset.min);
      var dmax = parseFloat(this.dataset.max);
      var minVal = isNaN(dmin) ? this.floor : this.clamp(dmin);
      var maxVal = isNaN(dmax) ? this.cap : this.clamp(dmax);
      if (minVal > maxVal) { var t = minVal; minVal = maxVal; maxVal = t; }

      [this.minRange, this.maxRange].forEach(function (r) {
        r.min = this.floor;
        r.max = this.cap;
        r.step = this.step;
      }, this);
      this.minRange.value = minVal;
      this.maxRange.value = maxVal;

      this.minRange.addEventListener('input', this.onInput.bind(this));
      this.maxRange.addEventListener('input', this.onInput.bind(this));
      this.minRange.addEventListener('change', this.onChange.bind(this));
      this.maxRange.addEventListener('change', this.onChange.bind(this));

      // The fill is positioned in px against the track width, so re-run it when
      // the track gains/changes width: on window resize and when the <details>
      // is opened (track has zero width while collapsed).
      this._boundRender = this.render.bind(this);
      window.addEventListener('resize', this._boundRender);
      this._details = this.closest('details');
      if (this._details) this._details.addEventListener('toggle', this._boundRender);

      this.render();
      this.syncHidden();
    }

    disconnectedCallback() {
      if (this._boundRender) {
        window.removeEventListener('resize', this._boundRender);
        if (this._details) this._details.removeEventListener('toggle', this._boundRender);
      }
    }

    clamp(v) {
      return Math.min(Math.max(v, this.floor), this.cap);
    }

    // Keep the thumbs from crossing — push whichever one is being dragged.
    fixCross() {
      var lo = parseFloat(this.minRange.value);
      var hi = parseFloat(this.maxRange.value);
      if (lo > hi) {
        if (document.activeElement === this.maxRange) this.minRange.value = hi;
        else this.maxRange.value = lo;
      }
    }

    onInput(e) {
      // Don't let a drag bubble up to the facet form's debounced submit.
      if (e && e.stopPropagation) e.stopPropagation();
      this.fixCross();
      this.render();
    }

    render() {
      var lo = parseFloat(this.minRange.value);
      var hi = parseFloat(this.maxRange.value);
      var span = this.cap - this.floor;
      var loFrac = (lo - this.floor) / span;
      var hiFrac = (hi - this.floor) / span;
      if (this.fill && this.track) {
        // A range thumb's centre travels from `radius` to `width - radius`, so
        // align the fill to the thumb centres (not 0–100%) — otherwise the green
        // bar pokes ~9px past each circle at the ends.
        var trackW = this.track.clientWidth;
        var usable = trackW - this.thumbRadius * 2;
        if (usable > 0) {
          var loPx = this.thumbRadius + loFrac * usable;
          var hiPx = this.thumbRadius + hiFrac * usable;
          this.fill.style.left = loPx + 'px';
          this.fill.style.width = Math.max(0, hiPx - loPx) + 'px';
        } else {
          // Track not laid out yet (e.g. collapsed) — fall back to %.
          this.fill.style.left = loFrac * 100 + '%';
          this.fill.style.width = (hiFrac - loFrac) * 100 + '%';
        }
      }
      if (this.minLabel) this.minLabel.textContent = this.symbol + Math.round(lo);
      if (this.maxLabel) {
        this.maxLabel.textContent = this.symbol + Math.round(hi) + (hi >= this.cap ? '+' : '');
      }
    }

    syncHidden() {
      var lo = parseFloat(this.minRange.value);
      var hi = parseFloat(this.maxRange.value);
      if (this.gte) {
        if (lo > this.floor) { this.gte.disabled = false; this.gte.value = lo; }
        else { this.gte.disabled = true; this.gte.value = ''; }
      }
      if (this.lte) {
        if (hi < this.cap) { this.lte.disabled = false; this.lte.value = hi; }
        else { this.lte.disabled = true; this.lte.value = ''; }
      }
    }

    onChange() {
      this.fixCross();
      this.render();
      this.syncHidden();
      // facets.js listens for a bubbling `input` on the facet form. Fire one
      // (manual dispatch works even on a disabled input) so it submits once.
      var target = this.gte || this.lte || this;
      target.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }

  customElements.define('fm-price-slider', FmPriceSlider);
})();
